import React from 'react';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import { FormattedMessage } from '../../../../util/reactIntl';
import { trimDisplayNameLastWord } from '../../../../util/text';
import { IconSpinner, Tooltip, Button } from '../../../../components';
import { ConditionalWrapper } from '../../../../util/common';
import css from './SectionContact.css';
import {
    getActionButtonStateFromTx,
    TRANSACTION_STATES_STATUS_ACCEPTED,
    TRANSACTION_STATES_STATUS_DECLINED,
    TRANSACTION_STATES_STATUS_ENQUIRED,
    txIsAccepted,
    txIsDeclined,
    txIsEnquired,
} from '../../../../util/transaction';
import { useSelector } from 'react-redux';
import { createResourceLocatorString } from '../../../../util/routes';
import routeConfiguration from '../../../../routeConfiguration';
import config from '../../../../config';
import { RIDER_AVAILABILITY_CONFIRMED } from '../../../../marketplace-custom-config';
import { ensureCurrentUser, ensureUser } from '../../../../util/data';

const { userTypeHorseowner } = config;

const getCurrentListingTransactions = listing => transaction =>
    transaction.relationships.listing?.data?.id?.uuid === listing?.id?.uuid ||
    transaction.attributes.protectedData.listingSubstitutionId === listing?.id?.uuid;

export default ({
    currentListing,
    riderListing,
    handleExternalListingContacting = () => null,
    handleListingContacting = () => null,
    loading,
    className,
}) => {
    const history = useHistory();
    const { location } = history;

    const currentUser = useSelector(s => s.user.currentUser);
    const listingAuthor = useSelector(s => s.ListingPage.listingAuthor);
    const allTransactions = useSelector(s => s.marketplaceData.entities.transaction);

    const authorId = listingAuthor?.id?.uuid;
    const currentUserId = currentUser?.id?.uuid;
    const isOwnListing = currentUserId && authorId && authorId === currentUserId;
    const userIsCurrentUser = listingAuthor && listingAuthor.type === 'currentUser';

    const { userType: currentUserType, availabilityStatus, profileComplete } =
        currentUser?.attributes?.profile?.publicData || {};

    const {
        attributes: {
            state: currentListingState,
            publicData: { externalListing },
        },
        id: { uuid: listingId },
    } = currentListing;

    const listingTransactions =
        typeof allTransactions === 'object'
            ? Object.values(allTransactions).filter(
                  getCurrentListingTransactions(currentListing),
                  {}
              )
            : {};

    const isClosed = currentListingState === 'closed';
    const currentUserIsOwner = currentUserType === userTypeHorseowner;
    const currentUserAvailable =
        availabilityStatus && availabilityStatus === RIDER_AVAILABILITY_CONFIRMED;

    const ensuredUser = userIsCurrentUser
        ? ensureCurrentUser(listingAuthor)
        : ensureUser(listingAuthor);

    const { displayName } = ensuredUser.attributes.profile;
    const authorDisplayName = trimDisplayNameLastWord(displayName);
    /**
     * horseowner navigates on the horse listing page
     */
    const ownersView = !isOwnListing && currentUserIsOwner && !userIsCurrentUser;
    /**
     * handle cases when user's availability is set to confirmed
     * but rider listing has not been opened yet
     */
    const riderListingReopeningInProgress =
        currentUserAvailable && profileComplete && !riderListing;

    const actionDisabed =
        isClosed || ownersView || isOwnListing || loading || riderListingReopeningInProgress;

    const { text: actionButtonText, state: actionButtonState } = getActionButtonStateFromTx(
        isOwnListing ? [] : listingTransactions,
        currentUser
    );

    const redirectToSignUpPage = () => {
        const { pathname, search, hash } = location;
        const state = { from: `${pathname}${search}${hash}` };

        // We need to log in before showing the modal, but first we need to ensure
        // that modal does open when user is redirected back to this listingpage
        history.push(
            createResourceLocatorString(
                'SignupRider',
                routeConfiguration(),
                {},
                listingId ? { listingId } : {}
            ),
            state
        );
    };

    const onContactUser = () => {
        if (!currentUser || !currentUserId) {
            return redirectToSignUpPage();
        }

        if (!!externalListing) {
            return handleExternalListingContacting();
        }

        if (isOwnListing) {
            return;
        }
        /**
         * Navigate a rider to an existing chat even if he is not available
         */
        const isDeclined = actionButtonState === TRANSACTION_STATES_STATUS_DECLINED;
        const isAccepted = actionButtonState === TRANSACTION_STATES_STATUS_ACCEPTED;
        const isEnquired = actionButtonState === TRANSACTION_STATES_STATUS_ENQUIRED;

        if (isDeclined || isAccepted || isEnquired) {
            const declinedOrAcceptedTx = listingTransactions.find(
                tx => txIsDeclined(tx) || txIsAccepted(tx) || txIsEnquired(tx)
            );

            history.push(
                createResourceLocatorString(
                    'OrderDetailsPage',
                    routeConfiguration(),
                    { id: declinedOrAcceptedTx.id.uuid },
                    {}
                )
            );
        }

        handleListingContacting();
    };

    const resolveActionId = () => {
        const ownersViewId =
            !isOwnListing &&
            currentUserIsOwner &&
            !userIsCurrentUser &&
            'horseownerViewsHorseownersProfile';

        const isClosedId = !ownersViewId && isClosed && 'noPublishedListingsForRider';

        return ownersViewId || isClosedId;
    };

    const actionId = resolveActionId();

    return (
        <ConditionalWrapper
            condition={!!actionId}
            wrapper={children => (
                <Tooltip
                    position="top"
                    content={
                        <>
                            <p>Anfragen nicht möglich</p>
                            <p>
                                <FormattedMessage
                                    id={`Inquiry.action.disabled.desc.${actionId}`}
                                    values={{
                                        displayName: authorDisplayName,
                                    }}
                                />
                            </p>
                        </>
                    }
                >
                    {children}
                </Tooltip>
            )}
        >
            <Button
                className={classNames(css.contactButton, css[actionButtonState], {
                    [className]: !!className,
                })}
                onClick={() => onContactUser()}
                disabled={actionDisabed}
            >
                {loading ? <IconSpinner /> : actionButtonText}
            </Button>
        </ConditionalWrapper>
    );
};
